import { Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslations } from "use-intl";
import { string } from "zod";

import { AddBaggageCard } from "@/app/ui/additional-services/AddBaggageCard";
import { SelectMealCard } from "@/app/ui/additional-services/SelectMealCard";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import { getAvailableServices } from "@/features/flights/utils/additionalServices";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import {
  SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES,
  SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES,
} from "@/utils/constants";

export const Route = createFileRoute(
  "/_authenticated/_flights/_flight-offer/flights/offer/$id/step/2/additional-services/",
)({
  validateSearch: (search: Record<string, unknown>) => {
    return { travellerId: string().parse(search.travellerId) };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { travellerId } = Route.useSearch();
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { data: travellers } = useQuery(travellersQueryOptions());
  const { getTravellerIndex } = useTravellersSelectionContext();
  const { selectedFlightPayload, getTravellerServices } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const traveller = travellers?.find((t) => t.id === travellerId);

  const servicesCategories = useMemo(() => {
    const services =
      traveller && singleFlightDetails
        ? getAvailableServices({
            traveller,
            travellerIndex: getTravellerIndex(traveller.id),
            optionalServices: singleFlightDetails.optionalServices,
            mandatoryServices: singleFlightDetails.mandatoryServices,
          })
        : [];

    const travellerServices = getTravellerServices(travellerId);
    const selectedServicesBookingCodes = travellerServices.map(
      (s) => s.service.bookingCode,
    );

    return services.map((s) => ({
      categoryCode: s.service.service.categoryCode,
      mandatory: s.mandatory,
      selected: selectedServicesBookingCodes.includes(s.service.bookingCode),
    }));
  }, [
    getTravellerIndex,
    getTravellerServices,
    singleFlightDetails,
    traveller,
    travellerId,
  ]);

  const baggageServices = servicesCategories.filter((sc) =>
    SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES.includes(sc.categoryCode),
  );
  const isBaggageRequired = baggageServices.some((bs) => bs.mandatory);
  const mealsServices = servicesCategories.filter((sc) =>
    SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES.includes(sc.categoryCode),
  );
  const isMealsRequired = mealsServices.some((bs) => bs.mandatory);

  if (!traveller || isAdditionalDataLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="airplane" />
      </PageLoader>
    );
  }

  return (
    <div className="flex min-h-full flex-col gap-4 px-main">
      <div>
        <div className="text-xl leading-7 font-medium text-text-title">
          {traveller?.firstName} {traveller?.lastName}
        </div>
        <div className="text-text-tertiary">
          {t("travellers.selectedAdditionalServicesForTraveller")}
        </div>
      </div>
      <div className="flex flex-col gap-5">
        {baggageServices.length > 0 ? (
          <AddBaggageCard
            required={isBaggageRequired}
            selectedAmount={baggageServices.filter((s) => s.selected).length}
          />
        ) : null}
        {mealsServices.length > 0 ? (
          <SelectMealCard
            required={isMealsRequired}
            selectedAmount={mealsServices.filter((s) => s.selected).length}
            onClick={() => {
              navigate({
                to: "./meal-selection",
                search: {
                  travellerId: traveller.id,
                },
              });
            }}
          />
        ) : null}
      </div>
      <PersistentDrawer>
        <Button
          disabled={isAdditionalDataLoading}
          fullWidth
          variant="contained"
          size="medium"
          sx={{
            marginTop: "auto",
          }}
          onClick={() => {
            navigate({ to: "../../3" });
          }}
        >
          {t("confirm")}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
